/**
 * BrowserTab — embedded multi-tab browser pane backed by CEF host OSR browsers.
 *
 * Each tab inside the pane gets its own host off-screen Chromium browser
 * (label `browser-<windowLabel>-<projectId>-<tabId>`) so X-Frame-Options sites
 * work and page state survives tab switches. The host composites each browser
 * into the tab's anchor rect; inactive tabs' browsers are host-hidden (the host
 * stops compositing them) — they stay alive, just not painting.
 *
 * (Originally these were Tauri child webviews floating over the DOM; that
 * overlay path + the vendored Linux multiwebview patch were removed in ticket
 * 031 once CEF host browsers, tickets 027–030, replaced it.)
 *
 * Per-project state in `project.settings.browser`:
 *  - `tabs`: open tabs (id, url, title)
 *  - `activeTabId`: which tab is focused
 *  - `bookmarks`: pinned per-project URLs shown above the page
 *  - `autoOpenDetected`: route detected localhost URLs into the active tab
 *
 * Web fallback: drops back to a single sandboxed iframe (no multi-tab,
 * no host features).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  ExternalLink,
  History,
  Star,
  Plus,
  Trash2,
  X,
  PictureInPicture2,
} from 'lucide-react';
import { useBackend, isCefHost, CefBackend } from '@/services/backend';
import type { HostBrowserControl } from '@/services/backend/cefBrowser';
import { getPopoutContext } from '@/lib/popoutContext';
import { useProjectStore } from '@/stores/projectStore';
import { useUIStore } from '@/stores/uiStore';
import { useDevServerStore } from '@/stores/devServerStore';
import {
  getProjectBrowserSettings,
  setProjectBrowserSettings,
  type BrowserBookmark,
  type BrowserTabState,
} from '@/lib/projectSettings';
import { useHostBrowser, type UseBrowserWebviewApi } from '@/lib/useBrowserWebview';
import { logger } from '@/lib/logger';

const TAG = 'BrowserTab';

interface BrowserTabProps {
  projectId?: string;
  projectPath?: string;
}

function genId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function deriveTitle(url: string): string {
  if (!url) return 'New tab';
  try {
    const u = new URL(url);
    return u.host + (u.pathname !== '/' ? u.pathname : '');
  } catch {
    return url;
  }
}

function sanitizeLabel(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]/g, '-').slice(0, 20);
}

/**
 * One mounted browser pane backed by a HOST OSR browser (CEF, ticket 030).
 * Drives the host's compositing browser via `useHostBrowser` + the CefBackend
 * browser control. The host composites the real page into this anchor's rect.
 * (The old Tauri child-webview pane was removed in ticket 031.)
 */
function HostBrowserPane({
  tab,
  webviewLabel,
  visible,
  ctl,
  context,
  onApi,
}: {
  tab: BrowserTabState;
  webviewLabel: string;
  visible: boolean;
  ctl: HostBrowserControl;
  /** The project's Claude identity → isolated cookie jar for claude.ai. */
  context?: string;
  onApi: (id: string, api: UseBrowserWebviewApi | null) => void;
}) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const overlayDepth = useUIStore((s) => s.overlayDepth);

  const api = useHostBrowser(ctl, {
    label: webviewLabel,
    url: tab.url,
    anchorRef,
    // Host-hide whenever this pane isn't the active tab OR a modal is open.
    forceHidden: !visible || overlayDepth > 0,
    context,
  });

  useEffect(() => {
    onApi(tab.id, api);
    return () => onApi(tab.id, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.id]);

  return (
    <div
      ref={anchorRef}
      className="flex-1 w-full bg-white"
      style={{ display: visible ? 'flex' : 'none' }}
      data-browser-anchor={webviewLabel}
    />
  );
}

export default function BrowserTab({ projectId, projectPath }: BrowserTabProps) {
  const backend = useBackend();
  const project = useProjectStore((s) => s.currentProject);
  const settings = useMemo(() => getProjectBrowserSettings(project?.settings ?? null), [project?.settings]);

  // Local mirror of persisted tabs — we don't want a save-round-trip
  // between every keystroke or tab click.
  const [tabs, setTabs] = useState<BrowserTabState[]>(() => {
    if (settings.tabs.length > 0) return settings.tabs;
    // Migration: if there's a legacy `lastUrl`, seed a single tab.
    if (settings.lastUrl) {
      return [{ id: genId('t'), url: settings.lastUrl, title: deriveTitle(settings.lastUrl) }];
    }
    return [];
  });
  const [activeTabId, setActiveTabId] = useState<string | null>(
    settings.activeTabId && tabs.some((t) => t.id === settings.activeTabId)
      ? settings.activeTabId
      : tabs[0]?.id ?? null,
  );
  const [autoOpen, setAutoOpen] = useState(settings.autoOpenDetected);
  const [bookmarks, setBookmarks] = useState<BrowserBookmark[]>(settings.bookmarks);
  // Latest tabs/active for the agent enumeration hook (registered once; reads
  // fresh state via these refs rather than re-registering on every change).
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const activeTabIdRef = useRef(activeTabId);
  activeTabIdRef.current = activeTabId;

  // Per-tab in-memory back/forward history. Persisting would be churn
  // for limited UX value — start fresh each session.
  const [historyByTab, setHistoryByTab] = useState<Record<string, { stack: string[]; idx: number }>>({});

  const [inputUrl, setInputUrl] = useState('');
  const [reloadKey, setReloadKey] = useState(0); // web fallback only
  const [showHistory, setShowHistory] = useState(false);
  const [showAddBookmark, setShowAddBookmark] = useState(false);
  const [bookmarkName, setBookmarkName] = useState('');

  // CEF host mode: drive host OSR browsers (030). The backend is a CefBackend
  // whose `.browser` is the host control surface. (The Tauri child-webview
  // overlay path was removed in ticket 031.)
  const cefMode = isCefHost();
  const hostCtl: HostBrowserControl | null =
    cefMode && backend instanceof CefBackend ? backend.browser : null;
  // "Embedded browser" path = a real native browser (CEF OSR) is composited
  // under the DOM. The web fallback (iframe) is when this is false.
  const embeddedMode = !!hostCtl;
  const stableProjectId = (projectId ?? project?.id ?? 'noproject');

  // The project's Claude identity name → an isolated browser cookie jar, so a
  // work-account artifact opens under that account's claude.ai session instead
  // of the shared/personal one. Resolved from .ateliercode/config.json
  // (authoritative, same source the core uses). Empty => shared default jar.
  const [claudeIdentity, setClaudeIdentity] = useState<string>('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await backend.files.readAteliercodeConfig(stableProjectId);
        const ident = raw ? (JSON.parse(raw).claudeIdentity as string | undefined) : undefined;
        if (!cancelled) setClaudeIdentity(typeof ident === 'string' ? ident : '');
      } catch {
        if (!cancelled) setClaudeIdentity('');
      }
    })();
    return () => { cancelled = true; };
  }, [backend, stableProjectId]);
  // Read once at top so closeTab / openNewTab / pop-out handlers can
  // reference it without TDZ ordering issues.
  const popoutCtx = getPopoutContext();

  // Imperative API per tab — populated by HostBrowserPane via callback.
  const apiByTab = useRef<Map<string, UseBrowserWebviewApi>>(new Map());
  const registerApi = useCallback((id: string, api: UseBrowserWebviewApi | null) => {
    if (api) apiByTab.current.set(id, api);
    else apiByTab.current.delete(id);
  }, []);

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? null;

  // Keep the URL bar synced to the active tab's URL.
  useEffect(() => {
    setInputUrl(activeTab?.url ?? '');
  }, [activeTab?.url, activeTabId]);

  // Re-mirror local state when the project switches under us.
  useEffect(() => {
    setTabs(settings.tabs.length > 0
      ? settings.tabs
      : settings.lastUrl
        ? [{ id: genId('t'), url: settings.lastUrl, title: deriveTitle(settings.lastUrl) }]
        : []);
    setActiveTabId(settings.activeTabId ?? settings.tabs[0]?.id ?? null);
    setBookmarks(settings.bookmarks);
    setAutoOpen(settings.autoOpenDetected);
    setHistoryByTab({});
    // Only re-sync on project identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  // Watch detected dev-server URLs for THIS project's path.
  const detectedHistory = useDevServerStore((s) => s.byProject.get(projectPath ?? ''));
  const latestDetected = detectedHistory?.[0]?.url ?? null;
  const detectedCount = detectedHistory?.length ?? 0;

  const persistSettings = useCallback(async (patch: Parameters<typeof setProjectBrowserSettings>[1]) => {
    if (!project) return;
    try {
      const settingsJson = setProjectBrowserSettings(project.settings, patch);
      await backend.projects.update(project.id, { settings: settingsJson });
    } catch (e) {
      logger.warn(TAG, 'Failed to persist BrowserTab settings', e);
    }
  }, [project, backend]);

  const persistTabs = useCallback((nextTabs: BrowserTabState[], nextActive: string | null) => {
    void persistSettings({ tabs: nextTabs, activeTabId: nextActive });
  }, [persistSettings]);

  const updateTab = useCallback((id: string, patch: Partial<BrowserTabState>) => {
    setTabs((prev) => {
      const next = prev.map((t) => (t.id === id ? { ...t, ...patch } : t));
      persistTabs(next, activeTabId);
      return next;
    });
  }, [activeTabId, persistTabs]);

  const navigateInTab = useCallback((id: string, rawUrl: string) => {
    const trimmed = rawUrl.trim();
    if (!trimmed) return;
    const next = /^https?:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;
    updateTab(id, { url: next, title: deriveTitle(next) });
    setHistoryByTab((prev) => {
      const cur = prev[id] ?? { stack: [], idx: -1 };
      const truncated = cur.stack.slice(0, cur.idx + 1);
      if (truncated[truncated.length - 1] === next) return prev;
      return {
        ...prev,
        [id]: { stack: [...truncated, next].slice(-50), idx: Math.min(cur.idx + 1, 49) },
      };
    });
    if (embeddedMode) {
      const api = apiByTab.current.get(id);
      void api?.navigate(next);
    }
  }, [embeddedMode, updateTab]);

  const navigateActive = useCallback((rawUrl: string) => {
    if (!activeTabId) {
      // No tabs yet — create one with this URL.
      const id = genId('t');
      const trimmed = rawUrl.trim();
      const url = /^https?:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;
      const next: BrowserTabState[] = [{ id, url, title: deriveTitle(url) }];
      setTabs(next);
      setActiveTabId(id);
      persistTabs(next, id);
      return;
    }
    navigateInTab(activeTabId, rawUrl);
  }, [activeTabId, navigateInTab, persistTabs]);

  useEffect(() => {
    if (!latestDetected || !autoOpen) return;
    if (activeTab?.url === latestDetected) return;
    logger.debug(TAG, `Auto-navigating active tab to detected dev server: ${latestDetected}`);
    navigateActive(latestDetected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestDetected, autoOpen]);

  const openNewTab = useCallback((url: string = '') => {
    const id = genId('t');
    const tab: BrowserTabState = { id, url, title: url ? deriveTitle(url) : 'New tab' };
    setTabs((prev) => {
      const next = [...prev, tab];
      persistTabs(next, id);
      return next;
    });
    setActiveTabId(id);
  }, [persistTabs]);

  // Agent-open hook: let the MCP browser bridge open a tab IN THIS PANEL (not a
  // popout window) and get back the webview label to drive. The core MCP's
  // browser_open tool evals `window.__atelierOpenBrowserTab(url)` on the main UI
  // webview; this returns the new tab's stable webview label synchronously so the
  // agent can immediately navigate/click/type it — even if the user isn't
  // currently looking at the panel (the OSR browser is created either way).
  useEffect(() => {
    const open = (rawUrl: string = ''): string => {
      const url = typeof rawUrl === 'string' ? rawUrl : '';
      const id = genId('t');
      const tab: BrowserTabState = { id, url, title: url ? deriveTitle(url) : 'New tab' };
      setTabs((prev) => {
        const next = [...prev, tab];
        persistTabs(next, id);
        return next;
      });
      setActiveTabId(id);
      return `browser-${sanitizeLabel(popoutCtx.windowLabel)}-${sanitizeLabel(stableProjectId)}-${sanitizeLabel(id)}`;
    };
    const w = window as unknown as {
      __atelierOpenBrowserTab?: (url?: string) => string;
      __atelierPendingBrowserOpen?: string;
      __atelierListBrowserTabs?: () => Array<{ label: string; url: string; title: string; active: boolean }>;
    };
    w.__atelierOpenBrowserTab = open;
    // Enumeration for the MCP browser_list tool — every open panel tab + its
    // webview label, so agents discover what they can drive.
    w.__atelierListBrowserTabs = () =>
      tabsRef.current.map((t) => ({
        label: `browser-${sanitizeLabel(popoutCtx.windowLabel)}-${sanitizeLabel(stableProjectId)}-${sanitizeLabel(t.id)}`,
        url: t.url,
        title: t.title,
        active: t.id === activeTabIdRef.current,
      }));
    // Cold-start: the agent asked to open a tab before this panel was mounted
    // (DockingWorkspace added the panel + stashed the url). Drain it now.
    if (typeof w.__atelierPendingBrowserOpen === 'string') {
      const pending = w.__atelierPendingBrowserOpen;
      delete w.__atelierPendingBrowserOpen;
      open(pending);
    }
    return () => {
      if (w.__atelierOpenBrowserTab === open) delete w.__atelierOpenBrowserTab;
      delete w.__atelierListBrowserTabs;
    };
  }, [persistTabs, popoutCtx.windowLabel, stableProjectId]);

  const closeTab = useCallback((id: string) => {
    // Explicit close — the underlying webview/host browser is destroyed and
    // won't come back via remount. The hook's unmount effect only hides (so
    // browsers survive project switches), so we close here for the user-
    // initiated close path.
    const webviewLabel = `browser-${sanitizeLabel(popoutCtx.windowLabel)}-${sanitizeLabel(stableProjectId)}-${sanitizeLabel(id)}`;
    if (hostCtl) {
      void hostCtl.close(webviewLabel).catch(() => { /* noop */ });
    }
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx === -1) return prev;
      const next = prev.filter((t) => t.id !== id);
      let nextActive = activeTabId;
      if (id === activeTabId) {
        nextActive = next[idx]?.id ?? next[idx - 1]?.id ?? null;
        setActiveTabId(nextActive);
      }
      persistTabs(next, nextActive);
      return next;
    });
    setHistoryByTab((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  }, [activeTabId, persistTabs, hostCtl, popoutCtx.windowLabel, stableProjectId]);

  const goBack = useCallback(() => {
    if (!activeTabId) return;
    const hist = historyByTab[activeTabId];
    if (!hist || hist.idx <= 0) return;
    const url = hist.stack[hist.idx - 1];
    setHistoryByTab((prev) => ({ ...prev, [activeTabId]: { ...hist, idx: hist.idx - 1 } }));
    updateTab(activeTabId, { url, title: deriveTitle(url) });
    if (embeddedMode) void apiByTab.current.get(activeTabId)?.navigate(url);
  }, [activeTabId, historyByTab, embeddedMode, updateTab]);

  const goForward = useCallback(() => {
    if (!activeTabId) return;
    const hist = historyByTab[activeTabId];
    if (!hist || hist.idx >= hist.stack.length - 1) return;
    const url = hist.stack[hist.idx + 1];
    setHistoryByTab((prev) => ({ ...prev, [activeTabId]: { ...hist, idx: hist.idx + 1 } }));
    updateTab(activeTabId, { url, title: deriveTitle(url) });
    if (embeddedMode) void apiByTab.current.get(activeTabId)?.navigate(url);
  }, [activeTabId, historyByTab, embeddedMode, updateTab]);

  const reload = useCallback(() => {
    if (!activeTabId) return;
    if (embeddedMode) void apiByTab.current.get(activeTabId)?.reload();
    else setReloadKey((k) => k + 1);
  }, [activeTabId, embeddedMode]);

  const openExternal = useCallback(() => {
    if (!activeTab?.url) return;
    window.open(activeTab.url, '_blank');
  }, [activeTab]);

  // "Pop out" — spawn a secondary OS window hosting this BrowserTab for
  // the current project. Only available in the main window (popouts
  // themselves don't pop out further). In the CEF host it routes through the
  // backend's popout API (a second GTK window + its own OSR browser, ticket 039).
  // popoutCtx is already read up at the top of the component.
  const canPopout = cefMode && !popoutCtx.isPopout && !!stableProjectId;
  const handlePopout = useCallback(async () => {
    if (!canPopout) return;
    try {
      if (cefMode && backend instanceof CefBackend) {
        const label = `popout-browser-${stableProjectId}`;
        await backend.popout.open(label, 'browser', stableProjectId);
      }
    } catch (e) {
      logger.warn(TAG, 'popout_open failed', e);
    }
  }, [canPopout, cefMode, backend, stableProjectId]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    navigateActive(inputUrl);
  }, [inputUrl, navigateActive]);

  const handleAddBookmark = useCallback(() => {
    const name = bookmarkName.trim();
    const bookmarkUrl = inputUrl.trim() || activeTab?.url || '';
    if (!bookmarkUrl) return;
    const next: BrowserBookmark[] = [
      ...bookmarks,
      { id: genId('bm'), name: name || deriveTitle(bookmarkUrl), url: bookmarkUrl },
    ];
    setBookmarks(next);
    setBookmarkName('');
    setShowAddBookmark(false);
    void persistSettings({ bookmarks: next });
  }, [bookmarkName, bookmarks, inputUrl, activeTab, persistSettings]);

  const handleRemoveBookmark = useCallback((id: string) => {
    const next = bookmarks.filter((b) => b.id !== id);
    setBookmarks(next);
    void persistSettings({ bookmarks: next });
  }, [bookmarks, persistSettings]);

  const handleAutoOpenChange = useCallback((next: boolean) => {
    setAutoOpen(next);
    void persistSettings({ autoOpenDetected: next });
  }, [persistSettings]);

  const switchTab = useCallback((id: string) => {
    setActiveTabId(id);
    persistTabs(tabs, id);
  }, [tabs, persistTabs]);

  const activeHistory = activeTabId ? historyByTab[activeTabId] : undefined;
  const canBack = (activeHistory?.idx ?? 0) > 0;
  const canForward = activeHistory ? activeHistory.idx < activeHistory.stack.length - 1 : false;

  return (
    <div className="flex flex-col h-full bg-base-100">
      {/* Tab strip */}
      <div className="flex items-stretch gap-px bg-base-300 border-b border-base-300 overflow-x-auto">
        {tabs.map((t) => {
          const active = t.id === activeTabId;
          return (
            <div
              key={t.id}
              className={`group flex items-center gap-1 pl-3 pr-1 min-w-32 max-w-56 shrink-0 cursor-pointer text-sm border-r border-base-300 ${
                active ? 'bg-base-100 text-base-content' : 'bg-base-200 text-base-content/70 hover:bg-base-100/60'
              }`}
              onClick={() => switchTab(t.id)}
              title={t.url || 'New tab'}
            >
              <span className="truncate flex-1 py-1.5">{t.title || deriveTitle(t.url)}</span>
              <button
                type="button"
                className="btn btn-ghost btn-xs btn-square opacity-0 group-hover:opacity-100"
                onClick={(e) => { e.stopPropagation(); closeTab(t.id); }}
                title="Close tab"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
        <button
          type="button"
          className="btn btn-ghost btn-xs btn-square shrink-0 self-center mx-1"
          onClick={() => openNewTab()}
          title="New tab"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Top toolbar */}
      <div className="flex items-center gap-1 px-2 py-1 border-b border-base-300 bg-base-200">
        <button type="button" className="btn btn-ghost btn-xs btn-square" onClick={goBack} disabled={!canBack} title="Back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <button type="button" className="btn btn-ghost btn-xs btn-square" onClick={goForward} disabled={!canForward} title="Forward">
          <ArrowRight className="w-4 h-4" />
        </button>
        <button type="button" className="btn btn-ghost btn-xs btn-square" onClick={reload} disabled={!activeTab?.url} title="Reload">
          <RefreshCw className="w-4 h-4" />
        </button>

        <form onSubmit={handleSubmit} className="flex-1 mx-1">
          <input
            type="text"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            placeholder="http://localhost:5173"
            className="input input-bordered input-xs w-full font-mono"
            spellCheck={false}
          />
        </form>

        <button
          type="button"
          className="btn btn-ghost btn-xs btn-square"
          onClick={() => { setBookmarkName(''); setShowAddBookmark((v) => !v); }}
          disabled={!inputUrl && !activeTab?.url}
          title="Bookmark this page"
        >
          <Star className="w-4 h-4" />
        </button>

        <div className="dropdown dropdown-end">
          <button
            type="button"
            className="btn btn-ghost btn-xs gap-1"
            onClick={() => setShowHistory((v) => !v)}
            title="Detected dev-server URLs"
          >
            <History className="w-4 h-4" />
            {detectedCount > 0 && <span className="text-xs">{detectedCount}</span>}
          </button>
          {showHistory && (
            <ul className="absolute right-0 top-full mt-1 menu bg-base-200 border border-base-300 rounded-md shadow-lg z-50 min-w-72 max-w-96">
              <li className="menu-title px-3 py-1 text-xs uppercase tracking-wide">Detected from terminals</li>
              {detectedHistory && detectedHistory.length > 0 ? (
                detectedHistory.map((entry) => (
                  <li key={entry.url}>
                    <button
                      type="button"
                      className="text-xs font-mono truncate block"
                      onClick={() => { navigateActive(entry.url); setShowHistory(false); }}
                      title={entry.url}
                    >
                      {entry.url}
                    </button>
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-xs text-base-content/60">
                  Run a dev server in a terminal — its URL shows up here.
                </li>
              )}
              <div className="divider my-1" />
              <li>
                <label className="label cursor-pointer justify-start gap-2 py-1">
                  <input
                    type="checkbox"
                    checked={autoOpen}
                    onChange={(e) => handleAutoOpenChange(e.target.checked)}
                    className="checkbox checkbox-xs checkbox-primary"
                  />
                  <span className="label-text text-xs">Auto-open detected URLs</span>
                </label>
              </li>
            </ul>
          )}
        </div>

        {canPopout && (
          <button
            type="button"
            className="btn btn-ghost btn-xs btn-square"
            onClick={handlePopout}
            title="Pop out into its own window"
          >
            <PictureInPicture2 className="w-4 h-4" />
          </button>
        )}
        <button type="button" className="btn btn-ghost btn-xs btn-square" onClick={openExternal} disabled={!activeTab?.url} title="Open in external browser">
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>

      {/* Add-bookmark inline form */}
      {showAddBookmark && (
        <div className="flex items-center gap-2 px-2 py-1 border-b border-base-300 bg-base-100">
          <input
            type="text"
            placeholder={(inputUrl || activeTab?.url) || 'URL'}
            value={bookmarkName}
            onChange={(e) => setBookmarkName(e.target.value)}
            className="input input-bordered input-xs flex-1"
            autoFocus
          />
          <button type="button" className="btn btn-primary btn-xs" onClick={handleAddBookmark}>
            <Plus className="w-3 h-3" /> Add
          </button>
          <button type="button" className="btn btn-ghost btn-xs btn-square" onClick={() => setShowAddBookmark(false)}>
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Bookmarks bar */}
      {bookmarks.length > 0 && (
        <div className="flex items-center gap-1 px-2 py-1 border-b border-base-300 bg-base-100 overflow-x-auto">
          {bookmarks.map((b) => (
            <div key={b.id} className="group flex items-center gap-1 shrink-0">
              <button
                type="button"
                className="btn btn-ghost btn-xs gap-1 max-w-44"
                onClick={() => navigateActive(b.url)}
                title={b.url}
              >
                <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                <span className="truncate text-xs">{b.name}</span>
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-xs btn-square opacity-0 group-hover:opacity-100"
                onClick={() => handleRemoveBookmark(b.id)}
                title="Remove bookmark"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Frame body */}
      {tabs.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-base-content/60 text-sm">
          <div className="text-center max-w-md px-6">
            <p className="font-medium text-base mb-2">No tabs open</p>
            <p className="mb-4">
              Click + above to open a new tab, or run a dev server in a terminal —
              detected URLs auto-fill here.
            </p>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => openNewTab()}>
              <Plus className="w-4 h-4" /> New tab
            </button>
          </div>
        </div>
      ) : embeddedMode && hostCtl ? (
        // All tabs mounted simultaneously so page state survives switches.
        // Inactive panes are display:none — the hook host-hides the underlying
        // browser (the host stops compositing it).
        <div className="flex-1 flex relative">
          {tabs.map((t) => {
            // Include the popout's window label in the namespace so a popout
            // window doesn't fight the main window for the same host browser id.
            // Main is 'main' by default. This label is the host browser id (030).
            const webviewLabel = `browser-${sanitizeLabel(popoutCtx.windowLabel)}-${sanitizeLabel(stableProjectId)}-${sanitizeLabel(t.id)}`;
            // Key by the FULL label (includes the project id), NOT just t.id.
            // BrowserTab stays mounted across a project switch — only the
            // projectId prop changes — so a t.id-only key would keep the same
            // pane mounted while its label silently changes, orphaning the old
            // project's browser as a frozen overlay that never gets hidden.
            // Keying by label forces unmount→remount on project switch, so the
            // unmount cleanup hides the old browser and the new one opens clean.
            return (
              <HostBrowserPane
                key={webviewLabel}
                tab={t}
                webviewLabel={webviewLabel}
                visible={t.id === activeTabId}
                ctl={hostCtl}
                context={claudeIdentity}
                onApi={registerApi}
              />
            );
          })}
        </div>
      ) : (
        // Web fallback: single iframe for the active tab only.
        activeTab?.url ? (
          <iframe
            key={`${activeTab.url}#${reloadKey}`}
            src={activeTab.url}
            className="flex-1 w-full bg-white"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-storage-access-by-user-activation"
            referrerPolicy="no-referrer"
            title="Project browser"
          />
        ) : (
          <div className="flex-1 bg-white" />
        )
      )}
    </div>
  );
}
